/** biome-ignore-all assist/source/organizeImports: <biomejs cannot parse this file for some reason> */
import AppBar from '@material-ui/core/AppBar/AppBar';
import Button from '@material-ui/core/Button/Button';
import Dialog from '@material-ui/core/Dialog/Dialog';
import DialogContent from '@material-ui/core/DialogContent/DialogContent';
import FormControl from '@material-ui/core/FormControl/FormControl';
import InputLabel from '@material-ui/core/InputLabel/InputLabel';
import Select from '@material-ui/core/Select/Select';
import Toolbar from '@material-ui/core/Toolbar/Toolbar';
import Typography from '@material-ui/core/Typography/Typography';
import { selectUserFranchise } from 'app/main/apps/franchises/ducks/franchises.selector';
import DialogCloseButton from 'app/main/design-system/components/DialogCloseButton';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useStores } from 'app/hooks/useStores';
import { getFranchiseTagsApi } from 'app/main/apps/franchises/api';
import { showMessage } from 'app/store/fuse/messageSlice';
import MenuItem from '@material-ui/core/MenuItem/MenuItem';
import { Alert } from '@material-ui/lab';
import { ArrowForwardIos } from '@material-ui/icons';
import { Link } from 'react-router-dom';
import { TagItemInDTO } from '../../type';

interface SelectTabletTagDialogProps {
  open: boolean;
  onClose: () => void;
}

const SelectTabletTagDialog: React.FC<SelectTabletTagDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const franchise = useSelector(selectUserFranchise);

  const [tags, setTags] = useState<TagItemInDTO[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState<string>(localStorage.getItem('selectedTagId') || '');

  const { currentStoreId } = useStores();

  const fetchTags = async () => {
    if (!franchise?.id) {
      return;
    }
    try {
      const response = await getFranchiseTagsApi(franchise.id, currentStoreId);
      setTags(response.data);
      setHasFetched(true);
      if (response.data.length === 1) {
        const { tagId } = response.data[0];
        setSelectedTagId(tagId);
        localStorage.setItem('selectedTagId', tagId);
      }
    } catch (error: any) {
      setHasFetched(true);
      if (error.response?.status !== 404) {
        dispatch(showMessage({ message: t('dialogs.tablet.fetchTagsError'), variant: 'error' }));
      } else if (open) {
        dispatch(showMessage({ message: t('dialogs.tablet.noTags'), variant: 'info' }));
      }
    }
  };

  const handleSelectTag = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    setSelectedTagId(value);
    localStorage.setItem('selectedTagId', value);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <Reset fetch state when store changes>
  useEffect(() => {
    setHasFetched(false);
    setTags([]);
    setSelectedTagId('');
  }, [currentStoreId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <fetchTags changes on every re-render and should not be used as a hook>
  useEffect(() => {
    if (open && currentStoreId && franchise?.id && !hasFetched) {
      fetchTags();
    }
  }, [open, currentStoreId, hasFetched, franchise?.id]);

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="select-scanner-dialog" fullWidth maxWidth="xs">
      <AppBar position="static" elevation={0}>
        <Toolbar className="px-24 flex justify-between">
          <Typography variant="h6" color="inherit">
            {t('dialogs.tablet.title')}
          </Typography>
          <DialogCloseButton onClose={onClose} />
        </Toolbar>
      </AppBar>
      <DialogContent className="flex flex-col items-center space-y-24 p-24">
        {tags.length > 0 ? (
          <FormControl variant="outlined" className="w-full" disabled={!currentStoreId || !tags.length}>
            <InputLabel id="tag-selector-label" className="bg-white px-2">
              {t('basic.fields.tags')}
            </InputLabel>
            <Select
              id="tag-selector"
              label={t('basic.fields.tags')}
              labelWidth={100}
              labelId="tag-selector-label"
              onChange={handleSelectTag}
              value={selectedTagId && tags.length ? selectedTagId : ''}
            >
              {tags.map(tag => (
                <MenuItem key={tag.id} value={tag.tagId} selected={tag.tagId === selectedTagId}>
                  {tag.name || tag.tagId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Alert severity="warning" className="w-full">
            {t('dialogs.tablet.noTagsMessage')}
          </Alert>
        )}
        <Button
          variant="contained"
          color="primary"
          disabled={!currentStoreId || !selectedTagId}
          className="w-full"
          component={Link}
          to={`/stores/${currentStoreId}/tags/${selectedTagId}/tablet`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('dialogs.tablet.accessToTablet')}
          <ArrowForwardIos fontSize="small" className="ml-4" />
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default SelectTabletTagDialog;
